import React, { useCallback, useEffect, useState } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { fetchSalaryStructureComponent } from "../../../Apis/Payroll";

const DEFAULT_VISIBLE_COLUMNS = [
  "payroll_component",
  "employee_name",
  "salary_structure",
  "calculation_method",
  "fixed_amount",
  "is_active",
];

const toText = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return value;
};

const employeeDisplayName = (employee) => {
  if (!employee || typeof employee !== "object") return "";

  const person =
    employee?.OrgPersons?.Person ||
    employee?.org_persons?.person ||
    employee?.person ||
    null;

  if (person) {
    return [person.first_name, person.middle_name, person.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
  }

  return (
    employee.employee_name ||
    employee.full_name ||
    employee.person_full_name ||
    employee.name ||
    [employee.first_name, employee.middle_name, employee.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    ""
  );
};

const flattenSalaryStructureComponent = (item) => {
  const payrollRel = item?.PayrollComponent ?? item?.payroll_component;
  const structureRel =
    item?.PayrollSlaryStructure ?? item?.payroll_slary_structure;

  const payrollName =
    typeof payrollRel === "string"
      ? payrollRel
      : payrollRel?.payroll_component_name ||
        item?.payroll_component_name ||
        item?.component_name ||
        "";

  const employee =
    structureRel && typeof structureRel === "object"
      ? structureRel.employee || structureRel.Employee || null
      : null;
  const cycle =
    structureRel && typeof structureRel === "object"
      ? structureRel.PayrollCycle ||
        structureRel.payroll_cycle ||
        structureRel.payrollCycle ||
        null
      : null;

  const employeeName =
    item?.employee_name || employeeDisplayName(employee) || "";
  const employeeCode =
    item?.employee_code || employee?.employee_code || "";
  const cycleName =
    typeof cycle === "string"
      ? cycle
      : item?.payroll_cycle || cycle?.payroll_cycle_name || "";
  const salaryBasis =
    item?.salary_basis ||
    (typeof structureRel === "object" ? structureRel?.salary_basis : "") ||
    "";

  const salaryStructure =
    item?.salary_structure ||
    [employeeName || employeeCode, cycleName, salaryBasis]
      .filter(Boolean)
      .join(" - ") ||
    (structureRel?.organization_payroll_employee_salary_structure_id
      ? `Salary Structure #${structureRel.organization_payroll_employee_salary_structure_id}`
      : "");

  const isActiveRaw = item?.is_active;
  const isActive =
    isActiveRaw === "✔" || isActiveRaw === "✖"
      ? isActiveRaw
      : isActiveRaw === true || isActiveRaw === 1 || isActiveRaw === "1"
        ? "✔"
        : "✖";

  return {
    id:
      item.organization_payroll_employee_salary_structure_component_id ??
      item.id,
    organization_payroll_employee_salary_structure_component_id:
      item.organization_payroll_employee_salary_structure_component_id ??
      item.id,
    payroll_component: payrollName,
    employee_name: employeeName,
    employee_code: employeeCode,
    payroll_cycle: cycleName,
    salary_basis: salaryBasis,
    salary_structure: salaryStructure,
    calculation_method: item?.calculation_method || "",
    fixed_amount: item?.fixed_amount ?? "",
    percentage_value: item?.percentage_value ?? "",
    percentage_of_component: item?.percentage_of_component ?? "",
    custom_formula_json: toText(item?.custom_formula_json),
    sort_order: item?.sort_order ?? "",
    is_active: isActive,
  };
};

function SalaryStructureComponentList() {
  const [loading, setLoading] = useState(true);
  const [Break, setBreak] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();

  useEffect(() => {
    if (!org?.organization_id) return;

    let cancelled = false;
    setLoading(true);

    fetchSalaryStructureComponent(org.organization_id)
      .then((data) => {
        const rows = data?.salarystructure ?? data?.data ?? data ?? [];
        const list = Array.isArray(rows) ? rows : [];
        if (!cancelled) {
          setBreak(list.map(flattenSalaryStructureComponent));
        }
      })
      .catch((err) => {
        console.error("Failed to fetch salary structure components:", err);
        if (!cancelled) {
          setBreak([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [org]);

  let deleteBreak = async (id) => {
    try {
      const org_id = org.organization_id;
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/payroll/salary-component/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          validateStatus: function (status) {
            return status >= 200 && status < 500;
          },
        }
      );

      if (response.status === 200) {
        toast.success(response.data.message);
      } else {
        const errorMessage =
          response.data.message ||
          response.data.errors?.[Object.keys(response.data.errors)[0]]?.[0] ||
          "Break";

        toast.error(errorMessage);
        console.warn("Deletion error:", response.status, response.data);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Delete failed:", error);
      toast.error("Something went wrong. Please try again later.");
    }
  };

  const handleEdit = useCallback(
    (item) => {
      navigate(`/payroll/salary-structure/components/edit/${item.id}`);
    },

    [navigate]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Salary Structure Component"}
        btnName={"Add Structure"}
        add_action={"PAYROLL_SALARY_COMPONENT_ADD"}
        Data={Break}
        delete_action={"ORG_STRUCTURE_DELETE"}
        tableHeaders={[
          {
            name: "Component",
            value_key: "payroll_component",
            textStyle: "capitalize",
          },
          {
            name: "Employee",
            value_key: "employee_name",
            textStyle: "capitalize",
          },
          {
            name: "Calculation Method",
            value_key: "calculation_method",
            textStyle: "capitalize",
          },
        ]}
        Icons={[
          <LocalPoliceIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <LocalPoliceIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Salary Structure Component",
          "Salary Structure Component",
          "Add Salary Structure Component",
          "Deleting this component will remove it from the salary structure",
        ]}
        Route={"/payroll/salary-structure/components"}
        setData={setBreak}
        DeleteFunc={deleteBreak}
      />

      <TableDataGeneric
        tableName="organization_payroll_employee_salary_structure_components"
        primaryKey="organization_payroll_employee_salary_structure_component_id"
        heading="Salary Structure Component"
        data={Break}
        sortname={"calculation_method"}
        edit_delete_action={[
          "PAYROLL_SALARY_COMPONENT_DELETE",
          "PAYROLL_SALARY_COMPONENT_EDIT",
        ]}
        showActions={true}
        Route="/payroll/salary-structure/components"
        filterModule="organization_payroll_employee_salary_structure_components"
        DeleteFunc={deleteBreak}
        EditFunc={handleEdit}
        token={localStorage.getItem("token")}
        organizationUserId={userData?.organization_user_id}
        config={{
          defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
          mandatoryColumns: ["payroll_component", "calculation_method"],
        }}
      />
    </>
  );
}

export default SalaryStructureComponentList;
